/**
 * Composable for managing navigation context and breadcrumb state
 * Uses Nuxt's history.state API with session storage fallback
 *
 * @example
 * // In source page (list, detail, etc.)
 * const { setNavigationContext } = useNavigationContext()
 * setNavigationContext({
 *   route: route.fullPath,
 *   displayName: 'Factures',
 *   data: { filters: currentFilters }
 * })
 * router.push('/invoices/123')
 *
 * // In destination page
 * const { getNavigationContext } = useNavigationContext()
 * const context = getNavigationContext()
 * // context.displayName = 'Factures'
 */

export interface NavigationContext {
  /** Full route path to navigate back to */
  route: string
  /** Display name for breadcrumb/back button */
  displayName: string
  /** Optional additional data (filters, ids, etc.) */
  data?: Record<string, any>
  /** Timestamp for cache invalidation */
  timestamp?: number
}

const STORAGE_KEY = 'ocv_navigation_context'
const CONTEXT_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

export function useNavigationContext() {
  const router = useRouter()

  /**
   * Store navigation context for the next page
   * Uses history.state (survives navigation) and session storage (survives refresh)
   */
  const setNavigationContext = (context: Omit<NavigationContext, 'timestamp'>) => {
    const fullContext: NavigationContext = {
      ...context,
      timestamp: Date.now()
    }

    // Store in session storage for page refresh scenarios
    if (import.meta.client) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fullContext))
    }

    // Note: history.state will be set automatically when navigating with router.push({ state: ... })
    // This function just ensures session storage is updated
  }

  /**
   * Get navigation context from history.state or session storage fallback
   * Automatically clears expired contexts
   */
  const getNavigationContext = (): NavigationContext | null => {
    if (!import.meta.client) {
      return null
    }

    // Try history.state first (set by router.push with state option)
    const historyContext = history.state?.navigationContext as NavigationContext | undefined
    if (historyContext && isValidContext(historyContext)) {
      return historyContext
    }

    // Fallback to session storage (for page refreshes)
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return null
    }

    try {
      const context = JSON.parse(stored) as NavigationContext
      if (isValidContext(context)) {
        return context
      } else {
        // Clear expired context
        clearNavigationContext()
        return null
      }
    } catch {
      return null
    }
  }

  /**
   * Check if context is still valid (not expired)
   */
  const isValidContext = (context: NavigationContext): boolean => {
    if (!context.timestamp) {
      return true // Legacy contexts without timestamp are considered valid
    }
    return Date.now() - context.timestamp < CONTEXT_TIMEOUT_MS
  }

  /**
   * Clear navigation context from storage
   */
  const clearNavigationContext = () => {
    if (import.meta.client) {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  }

  /**
   * Navigate to a route with navigation context
   * This is a convenience wrapper around router.push that sets context
   */
  const navigateWithContext = (
    to: string,
    context: Omit<NavigationContext, 'timestamp'>
  ) => {
    setNavigationContext(context)
    return router.push({
      path: to,
      state: {
        navigationContext: {
          ...context,
          timestamp: Date.now()
        }
      }
    })
  }

  /**
   * Navigate back to the stored context route, or use router.back() as fallback
   */
  const navigateBack = () => {
    const context = getNavigationContext()

    if (context?.route) {
      router.push(context.route)
    } else {
      router.back()
    }
  }

  return {
    setNavigationContext,
    getNavigationContext,
    clearNavigationContext,
    navigateWithContext,
    navigateBack
  }
}
